"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/shadcn/ui/button";
import { Input } from "@/shadcn/ui/input";
import { CornerButton } from "@/shadcn/ui/corner-button";

// Forgot password schema
const forgotPasswordSchema = z.object({
  email: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein"),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

interface ResetPasswordFormProps {
  onSuccess?: () => void;
  onBackToSignIn?: () => void;
}

export default function ResetPasswordForm({
  onSuccess,
  onBackToSignIn,
}: ResetPasswordFormProps) {
  const { recoverPassword, isLoading } = useAuth();

  const forgotPasswordForm = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    const response = await recoverPassword(data.email);

    // If password recovery was successful, call onSuccess callback
    if (response.success && onSuccess) {
      onSuccess();
    }
  };

  return (
    <form onSubmit={forgotPasswordForm.handleSubmit(onSubmit)} className="">
      <div>
        <label
          htmlFor="forgotEmail"
          className="block text-[1.75rem] text-black/80 mb-2"
        >
          E-Mail-Adresse
        </label>
        <Input
          type="email"
          id="forgotEmail"
          {...forgotPasswordForm.register("email")}
          placeholder="your@email.com"
          className={cn(
            "rounded-[.75rem] border border-black/20 !text-black placeholder:text-black/40 caret-black/50 ring-0 outline-none focus:border-black/40 transition-colors",
            forgotPasswordForm.formState.errors.email && "!border-red-500",
          )}
        />
        {forgotPasswordForm.formState.errors.email && (
          <p className="mt-1 text-sm text-red-600 mb-0">
            {forgotPasswordForm.formState.errors.email.message}
          </p>
        )}
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-[.75rem] p-[2rem] my-[2rem]">
        <p className="text-sm text-amber-700 mb-2">
          <strong>📧 Anleitung zum Zurücksetzen des Passworts</strong>
        </p>
        <p className="text-sm text-amber-700 mb-0">
          Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum
          Zurücksetzen Ihres Passworts.
        </p>
      </div>

      <CornerButton
        className="[clip-path:polygon(0_0,100%_0,100%_calc(100%-2rem),calc(100%-2rem)_100%,0_100%)] !bg-black w-full"
        type="dark"
        htmlType="submit"
        disabled={isLoading}
      >
        <div className="corner-button-text relative">
          <div className="corner-button-text-initial transition-all duration-300">
            {isLoading ? "Wird gesendet…" : "Link zum Zurücksetzen senden"}
          </div>
          <div className="corner-button-text-hover absolute top-[3rem] transition-all duration-300 pointer-events-none">
            {isLoading ? "Wird gesendet…" : "Link zum Zurücksetzen senden"}
          </div>
        </div>
      </CornerButton>

      {onBackToSignIn && (
        <Button type="button" onClick={onBackToSignIn} className="w-full">
          Zurück zur Anmeldung
        </Button>
      )}
    </form>
  );
}
